import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { getAdminSiteSettings, getPublicSiteSettings, putAdminSiteSettings, postMailTest } from "@/lib/adminApi";
import { SETTINGS_FIELDS, validateSettings, settingsPayload, apiErrorText, mailTestView, paymentsStatusView } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

export default function Services() {
  useNoindex();
  const [form, setForm] = useState(null);
  const [freeLeft, setFreeLeft] = useState(null);
  const [payEnabled, setPayEnabled] = useState(false);
  const [payLive, setPayLive] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailView, setMailView] = useState(null);

  const load = async () => {
    try {
      const [s, pub] = await Promise.all([getAdminSiteSettings(), getPublicSiteSettings()]);
      setForm(Object.fromEntries(SETTINGS_FIELDS.map((f) => [f.key, s[f.key]])));
      setFreeLeft(pub.free_slots_left);
      setPayEnabled(s.payments_enabled === true);
      setPayLive(pub.payments_live === true);
    } catch (e) {
      toast.error(apiErrorText(e, "Ayarlar yüklenemedi"));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const errs = validateSettings(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const saved = await putAdminSiteSettings(settingsPayload(form));
      setForm(Object.fromEntries(SETTINGS_FIELDS.map((f) => [f.key, saved[f.key]])));
      toast.success("Ayarlar kaydedildi");
      const pub = await getPublicSiteSettings();
      setFreeLeft(pub.free_slots_left);
    } catch (err) {
      toast.error(apiErrorText(err));
    } finally { setBusy(false); }
  };

  const togglePayments = async (next) => {
    setPayBusy(true);
    try {
      const saved = await putAdminSiteSettings({ payments_enabled: next === true });
      setPayEnabled(saved.payments_enabled === true);
      const pub = await getPublicSiteSettings();
      setPayLive(pub.payments_live === true);
      toast.success(next ? "Ödemeler etkinleştirildi" : "Ödemeler kapatıldı");
    } catch (err) {
      toast.error(apiErrorText(err));
    } finally { setPayBusy(false); }
  };

  const runMailTest = async () => {
    if (mailBusy) return;
    setMailBusy(true);
    setMailView(null);
    try {
      setMailView(mailTestView(await postMailTest()));
    } catch (err) {
      setMailView({ ...mailTestView(null), ok: false, message: `Gönderilemedi: ${apiErrorText(err, "İstek başarısız")}` });
    } finally { setMailBusy(false); }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="mb-4 text-xl font-semibold">Hizmetler ve fiyatlar</h1>
      {!form && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {form && (
        <form onSubmit={save} className="space-y-4" data-testid="settings-form" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            {SETTINGS_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`s-${f.key}`}>{f.label}</Label>
                <Input
                  id={`s-${f.key}`}
                  inputMode="numeric"
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  aria-invalid={!!errors[f.key]}
                />
                {errors[f.key] && <p className="text-xs text-red-600">{errors[f.key]}</p>}
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Kalan ücretsiz kontenjan (hesaplanan)</Label>
              <Input value={freeLeft === null ? "-" : freeLeft} readOnly disabled data-testid="free-slots-left" />
            </div>
          </div>
          <Button type="submit" disabled={busy}>Kaydet</Button>
        </form>
      )}
      {form && (
        <section className="mt-8 rounded-md border p-4" data-testid="payments-card">
          <h2 className="mb-2 text-base font-semibold">Ödemeler (Stripe)</h2>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={payEnabled} onCheckedChange={togglePayments} disabled={payBusy} data-testid="payments-toggle" />
            Ödemeleri etkinleştir (payments_enabled)
          </label>
          {(() => {
            const v = paymentsStatusView({ paymentsEnabled: payEnabled, paymentsLive: payLive });
            return (
              <p className={`mt-3 text-sm ${v.live ? "text-green-700" : "text-amber-800"}`} data-testid="payments-status">
                <strong>payments_live: {v.live ? "true" : "false"}.</strong> {v.text}
              </p>
            );
          })()}
        </section>
      )}
      <section className="mt-8 rounded-md border p-4" data-testid="mail-test-card">
        <h2 className="mb-2 text-base font-semibold">E-posta (Resend)</h2>
        <Button type="button" variant="outline" onClick={runMailTest} disabled={mailBusy}>
          {mailBusy ? "Gönderiliyor..." : "Test-E-Mail senden"}
        </Button>
        {mailView && (
          <div className="mt-3 text-sm" data-testid="mail-test-result">
            <p className={mailView.ok ? "text-green-700" : "text-red-600"}>{mailView.message}</p>
            {!mailView.ok && (
              <ul className="mt-2 space-y-1">
                {mailView.checklist.map((c) => (
                  <li key={c.name} className={c.ok ? "text-green-700" : "text-red-600"}>
                    {c.ok ? "✓" : "✗"} {c.name}{c.ok ? "" : " (eksik)"}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
